import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./tableViews.css";

import { ColumnDef, Row, SortingState, createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtual } from "react-virtual";

// UI Imports
import Skeleton from "../../components/skeleton/Skeleton";
import HeaderUnit from "../../components/header/HeaderUnit";
import GenericCell from "../../components/cell/GenericCell";
import BatteryCell from "../../components/cell/battery/BatteryCell";
import LastSyncTimeCell from "../../components/cell/lastSyncedTime/LastSyncTimeCell";

import {convertTimeString, getUsLocaleString} from "../../utils/TimeUtils"

import ActionCell from "../../components/cell/action/ActionCell";
import copyClipboardIcon from "../../assets/Indicator.svg";

import SeeMoreCell from "../../components/cell/action/SeeMoreCell";

import ModalOverlay from "../../components/modal/ModalOverlay";

import ParticipantInfoModal from "../../pages/participants/participantInfoDetail/participantInfoModal"
import SensorInfoModal from "../../pages/sensors/sensorInfoDetail/sensorInfoModal"

type SensorDetails = {
    participantID: string; // Example: 'LAS001NNN'
    healthScore: number; // Value from 1 to 10
    sensorID: string; // 12 digit Hexadecimal Format
    sensorBattery: number; // Value from 0 - 100
    lastSyncTime: string; // Time data type
    firstSyncTime: string; // Time data type
    provisionTime: string; // Time data type
    organizationName: string; // Example: 'org-A'
    studyName: string; // Example: 'study-A'
    pinnedStatus: boolean;
    syncStatus: string;
};

type TableOptions = {
  tabName: string;
  isStudySelected: boolean;
  isStudyOrgSelected: boolean;
  networkError: boolean;
  isLoading: boolean;
}

type TableDataProps = {
    TableDataArray: SensorDetails[]; // Adjust the type based on your array elements
    TableOptions: TableOptions;
};




const TableViews: React.FC<TableDataProps> = ({ TableDataArray, TableOptions }) => {

    const [isLoading, setLoading] = useState(false);
    

    const scrollRef = useRef<HTMLDivElement>(null);
    const [isTableHorizontallyScrolled, setTableHorizontallyScrolled] = useState(false);

    const columnHelper = createColumnHelper<SensorDetails>();
    const [data, setData] = useState<SensorDetails[]>([]);

    const [isModalVisible, setModalVisible] = useState(false);

    const [selectedParticipant, setSelectedParticipant] = useState<SensorDetails | null>(null);

    useEffect(() => {
        setData(TableDataArray);
    }, [TableDataArray]);

    useEffect(() => {
      setLoading(false)
    }, [TableOptions.isLoading])

    function copyToClipboard(text: string) {
        navigator.clipboard.writeText(text);
    }

    const columns = useMemo<ColumnDef<SensorDetails, any>[]>(
        () => [

          TableOptions.tabName === "Participants" 
            ? columnHelper.accessor(row => row.participantID, {
                id: "participantID",
                cell: (info) => (isLoading 
                    ? <Skeleton></Skeleton> 
                    : <ActionCell
                        icon={copyClipboardIcon}
                        tooltipText="Copied!"
                        onClicked={() => {
                          copyToClipboard(info.getValue());
                        }}
                        value={info.getValue()}
                      ></ActionCell>),
                header: (header) => (
                    <HeaderUnit
                    value={"Participant ID"}
                    isSorted={header.column.getIsSorted()}
                    onClicked={function (): void {
                        header.column.toggleSorting();
                    }}
                    ></HeaderUnit>
                ),
                size: 168,
              }) 
            : columnHelper.accessor(row => row.sensorID, {
              id: "sensorID",
              cell: (info) => (isLoading 
                ? <Skeleton></Skeleton> 
                : <ActionCell
                    icon={copyClipboardIcon}
                    tooltipText="Copied!"
                    onClicked={() => {
                      copyToClipboard(info.getValue());
                    }}
                    value={info.getValue()}
                  ></ActionCell>),
              header: (header) => (
                <HeaderUnit
                  value={"Sensor ID"}
                  isSorted={header.column.getIsSorted()}
                  onClicked={function (): void {
                    header.column.toggleSorting();
                  }}
                ></HeaderUnit>
              ),
              size: 168,
            }),

            TableOptions.tabName === "Participants" ?
            columnHelper.accessor(row => row.sensorID, {
                id: "sensorID",
                cell: (info) => (isLoading 
                    ? <Skeleton></Skeleton> 
                    : <ActionCell
                        icon={copyClipboardIcon}
                        tooltipText="Copied!"
                        onClicked={() => {
                          copyToClipboard(info.getValue());
                        }}
                        value={info.getValue()}
                      ></ActionCell>),
                header: (header) => (
                  <HeaderUnit
                    value={"Sensor ID"}
                    isSorted={header.column.getIsSorted()}
                    onClicked={function (): void {
                      header.column.toggleSorting();
                    }}
                  ></HeaderUnit>
                ),
                size: 168,
            }) : columnHelper.accessor(row => row.participantID, {
              id: "participantID",
              cell: (info) => (isLoading 
                ? <Skeleton></Skeleton> 
                : <ActionCell
                    icon={copyClipboardIcon}
                    tooltipText="Copied!"
                    onClicked={() => {
                      copyToClipboard(info.getValue());
                    }}
                    value={info.getValue()}
                  ></ActionCell>),
              header: (header) => (
                  <HeaderUnit
                  value={"Attached Participant"}
                  isSorted={header.column.getIsSorted()}
                  onClicked={function (): void {
                      header.column.toggleSorting();
                  }}
                  ></HeaderUnit>
              ),
              size: 240,
          }),

        //   ...(TableOptions.tabName === "Participants" ? [ 
        //     columnHelper.accessor(row => row.healthScore, {
        //       id: "healthScore",
        //       cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={String(info.getValue())}></GenericCell>),
        //       header: (header) => (
        //         <HeaderUnit
        //           value={"Health Score"}
        //           isSorted={header.column.getIsSorted()}
        //           onClicked={function (): void {
        //             header.column.toggleSorting();
        //           }}
        //         ></HeaderUnit>
        //       ),
        //       size: 168,
        //   })
        // ]: []),

        ...(TableOptions.isStudySelected === false ? [columnHelper.accessor(row => row.studyName, {
            id: "studyName",
            cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={info.getValue()}></GenericCell>),
            header: (header) => (
            <HeaderUnit
                value={"Study ID"}
                isSorted={header.column.getIsSorted()}
                onClicked={function (): void {
                header.column.toggleSorting();
                }}
            ></HeaderUnit>
            ),  
            size: 208,
        })] : []),

        ...(TableOptions.isStudyOrgSelected === false ? [ columnHelper.accessor(row => row.organizationName, {
          id: "organizationName",
          cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={info.getValue()}></GenericCell>),
          header: (header) => (
          <HeaderUnit
              value={"Organization Name"}
              isSorted={header.column.getIsSorted()}
              onClicked={function (): void {
              header.column.toggleSorting();
              }}
          ></HeaderUnit>
          ),
          size: 208,
      })] : []),


          


            // columnHelper.accessor(row => row.sensorBattery, {
            //     id: "battery",
            //     cell: (info) => (isLoading ? <Skeleton></Skeleton> : <BatteryCell value={info.getValue()}></BatteryCell>),
            //     header: (header) => (
            //       <HeaderUnit
            //         value={"Sensor Battery"}
            //         isSorted={header.column.getIsSorted()}
            //         onClicked={function (): void {
            //           header.column.toggleSorting();
            //         }}
            //       ></HeaderUnit>
            //     ),
  
            //     size: 148,
            // }),

            columnHelper.accessor(row => {
                  // Use Date.parse() to validate and get the timestamp in milliseconds
                  const parsedTimestamp = Date.parse(row.lastSyncTime);
                  return { syncTime: isNaN(parsedTimestamp) ? null : new Date(parsedTimestamp) };
                }, {
                id: "lastSyncTime",
                cell: (info) => (isLoading ? <Skeleton></Skeleton> : <LastSyncTimeCell value={info.getValue().syncTime}></LastSyncTimeCell>),
                header: (header) => (
                <HeaderUnit
                    value={"Last Synced Time"}
                    isSorted={header.column.getIsSorted()}
                    onClicked={function (): void {
                    header.column.toggleSorting();
                    }}
                ></HeaderUnit>
                ),
                size: 208,
                // Custom sorting function to handle null values
                sortingFn: (rowA, rowB) => {
                  const dateA = rowA.original.lastSyncTime ? new Date(Date.parse(rowA.original.lastSyncTime)) : null;
                  const dateB = rowB.original.lastSyncTime ? new Date(Date.parse(rowB.original.lastSyncTime)) : null;
          
                  if (dateA === null && dateB === null) {
                      return 0;
                  } else if (dateA === null) {
                      return 1; // Null values at the bottom
                  } else if (dateB === null) {
                      return -1;
                  } else {
                      return dateB.getTime() - dateA.getTime(); // Convert Date objects to numbers
                  }
                },
            }),
            // row.firstSyncTime
            columnHelper.accessor(row => {
              // Use Date.parse() to validate and get the timestamp in milliseconds
              const parsedTimestamp = Date.parse(row.firstSyncTime);
              return { syncTime: isNaN(parsedTimestamp) ? null : new Date(parsedTimestamp) };
            }, {
                id: "firstSyncTime",
                cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={getUsLocaleString(info.getValue().syncTime)}></GenericCell>),
                header: (header) => (
                <HeaderUnit
                    value={"First Synced Time"}
                    isSorted={header.column.getIsSorted()}
                    onClicked={function (): void {
                    header.column.toggleSorting();
                    }}
                ></HeaderUnit>
                ),
                size: 208,
                // Custom sorting function to handle null values
                sortingFn: (rowA, rowB) => {
                  const dateA = rowA.original.firstSyncTime ? new Date(Date.parse(rowA.original.firstSyncTime)) : null;
                  const dateB = rowB.original.firstSyncTime ? new Date(Date.parse(rowB.original.firstSyncTime)) : null;
          
                  if (dateA === null && dateB === null) {
                      return 0;
                  } else if (dateA === null) {
                      return 1; // Null values at the bottom
                  } else if (dateB === null) {
                      return -1;
                  } else {
                      return dateB.getTime() - dateA.getTime(); // Convert Date objects to numbers
                  }
                },
            }),
            // row.provisionTime
            columnHelper.accessor(row => {
              // Use Date.parse() to validate and get the timestamp in milliseconds
              const parsedTimestamp = Date.parse(row.provisionTime);
              return { syncTime: isNaN(parsedTimestamp) ? null : new Date(parsedTimestamp) };
            }, {
                id: "provisionTime",
                cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={getUsLocaleString(info.getValue().syncTime)}></GenericCell>),
                header: (header) => (
                <HeaderUnit
                    value={"Provision Time"}
                    isSorted={header.column.getIsSorted()}
                    onClicked={function (): void {
                    header.column.toggleSorting();
                    }}
                ></HeaderUnit>
                ),
                size: 208,
                // Custom sorting function to handle null values
                sortingFn: (rowA, rowB) => {
                  const dateA = rowA.original.provisionTime ? new Date(Date.parse(rowA.original.provisionTime)) : null;
                  const dateB = rowB.original.provisionTime ? new Date(Date.parse(rowB.original.provisionTime)) : null;
          
                  if (dateA === null && dateB === null) {
                      return 0;
                  } else if (dateA === null) {
                      return 1; // Null values at the bottom
                  } else if (dateB === null) {
                      return -1;
                  } else {
                      return dateB.getTime() - dateA.getTime(); // Convert Date objects to numbers
                  }
                },
            }),

            columnHelper.accessor(row => row, {
              id: " ",
              cell: (info) => (isLoading 
                  ? <Skeleton></Skeleton> 
                  : <SeeMoreCell
                      onClicked={() => {
                        setSelectedParticipant(info.getValue());
                        setModalVisible(true);
                      }}
                    ></SeeMoreCell>),
              
              size: 120,
            })

        ], [columnHelper, isLoading]);

        // The order of the above will change in sensors page

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableSorting: true,
        debugTable: true,
    })

    const { rows } = table.getRowModel();
    const rowVirtualizer = useVirtual({
        parentRef: scrollRef,
        size: rows.length,
        overscan: 10,
    });

    const { virtualItems: virtualRows, totalSize } = rowVirtualizer;

    const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
    const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  return (
    <>
        <div
          className="data-table"
          ref={scrollRef}
          onScroll={() => {
            if (scrollRef.current?.scrollLeft !== 0) {
              setTableHorizontallyScrolled(true);
            } else {
              setTableHorizontallyScrolled(false);
            }
          }}
        >
          {data.length !== 0 ? (
            <table
              style={{
                width: table.getCenterTotalSize(),
              }}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th className={isTableHorizontallyScrolled && TableOptions.tabName === "Participants" && header.id === "participantID" ? "header-item drop-shadow" : isTableHorizontallyScrolled && TableOptions.tabName === "Sensors" && header.id === "sensorID" ? "header-item drop-shadow" : "header-item"} key={header.id} style={{ width: header.getSize() }}>
                        {header.isPlaceholder ? null : <>{flexRender(header.column.columnDef.header, header.getContext())}</>}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {paddingTop > 0 && (
                  <tr>
                    <td style={{ height: `${paddingTop}px` }} />
                  </tr>
                )}
                {virtualRows.map((virtualRow) => {
                  const row = rows[virtualRow.index] as Row<SensorDetails>;

                  return (
                    <tr key={row.id} className="Random">
                      {row.getVisibleCells().map((cell) => (
                        <td className={isTableHorizontallyScrolled && TableOptions.tabName === "Participants" && cell.id.includes("participantID") ? "drop-shadow" : isTableHorizontallyScrolled && TableOptions.tabName === "Sensors" && cell.id.includes("sensorID") ? "drop-shadow" : ""} key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {paddingBottom > 0 && (
                  <tr>
                    <td style={{ height: `${paddingBottom}px` }} />
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="no-data body-large">
                Loading data...
               {/* There are no devices in this category. */}
               {/* Add Error Status here */}
            </div>
          )}
        </div>

        {isModalVisible && (
        <ModalOverlay
          closeModal={() => {
            setModalVisible(false);
          }}
        >

          {TableOptions.tabName === "Participants" ? 
            <ParticipantInfoModal
              index={-1}
              
              onCloseClicked={() => {    
                setModalVisible(false);
              }}
            ></ParticipantInfoModal>
            
            :

            <SensorInfoModal
              data={selectedParticipant}
              onCloseClicked={() => {    
                setModalVisible(false);
              }}
            ></SensorInfoModal>



          }
          
        </ModalOverlay>
      )}

    </>
  );
};

export default TableViews;
